import type { CSSProperties, ReactNode } from "react";

/**
 * Visual tone for the DevMenu rows.
 *
 * - `hubble` (default): compact rows, 13px medium secondary text,
 *   small horizontal padding. Matches Hubble's existing nav user menu.
 * - `cargo`: matches Cargo's existing styled-components MenuItem — 12px
 *   vertical / 32px horizontal padding, 14px semibold blue text, hover
 *   swap to blue background with white text. Pass this in dashboard so
 *   the dev rows align visually with the Sign Out / Switch Organization
 *   items they share a dropdown with.
 */
export type DevMenuTone = "hubble" | "cargo";

const BASE_CLASSES =
  "flex w-full cursor-pointer items-center justify-between text-left transition-colors";

// Each row owns its own background — hosts (e.g. Cargo's MenuContainer) don't
// always set one, and without `bg-card` the page content behind the menu
// bleeds through gaps and through the rows themselves.
const TONE_ROW_CLASSES: Record<
  DevMenuTone,
  { base: string; indented: string }
> = {
  hubble: {
    base: "bg-card px-3 py-1.5 text-sm leading-tight font-medium text-content-secondary hover:bg-nav-bg hover:text-white",
    indented:
      "bg-card px-3 py-1.5 pl-5 text-sm leading-tight font-medium text-content-secondary hover:bg-nav-bg hover:text-white",
  },
  cargo: {
    base: "bg-card px-8 py-3 text-sm leading-tight font-semibold text-blue hover:bg-nav-bg hover:text-white",
    indented:
      "bg-card px-8 py-3 pl-12 text-sm leading-tight font-semibold text-blue hover:bg-nav-bg hover:text-white",
  },
};

// Inline font-size as a belt-and-suspenders override. Hubble/Dashboard each
// load Bootstrap and dashboard has a global `* { font-family: ... }` rule
// that, depending on cascade ordering, has ended up making the in-button
// text inherit a larger size than the surrounding Cargo MenuItem. Inline
// styles beat both layered Tailwind utilities and unlayered Bootstrap
// rules, so the row visually matches Cargo MenuItem (14px / weight 600)
// regardless of which CSS happens to land first.
const TONE_INLINE_STYLES: Record<DevMenuTone, CSSProperties> = {
  hubble: { fontSize: 14, fontWeight: 500 },
  cargo: { fontSize: 14, fontWeight: 600 },
};

const TONE_VALUE_CLASSES: Record<DevMenuTone, string> = {
  hubble: "text-xs text-muted-foreground",
  cargo: "text-xs font-normal opacity-70",
};

function rowClassName(
  tone: DevMenuTone,
  indented: boolean | undefined,
): string {
  const t = TONE_ROW_CLASSES[tone];
  return `${BASE_CLASSES} ${indented ? t.indented : t.base}`;
}

const TONE_HEADING_CLASSES: Record<DevMenuTone, string> = {
  hubble:
    "px-3 pt-2 pb-1 text-[10px] font-bold tracking-wider text-faint uppercase",
  cargo:
    "px-8 pt-3 pb-1 text-[10px] font-bold tracking-wider text-faint uppercase",
};

/**
 * A small uppercase section label (e.g. "Display") that groups a run of rows
 * inside the menu. Matches the API Environment label styling.
 */
export function DevSectionHeading({
  children,
  tone = "hubble",
}: {
  children: ReactNode;
  tone?: DevMenuTone | undefined;
}) {
  return <div className={TONE_HEADING_CLASSES[tone]}>{children}</div>;
}

interface DevMenuRowProps {
  onClick?: (() => void) | undefined;
  children: ReactNode;
  /**
   * Indent the row, used for items nested inside a "Developer Settings"
   * sub-section so they read as a group.
   */
  indented?: boolean | undefined;
  tone?: DevMenuTone | undefined;
}

export function DevMenuRow({
  onClick,
  children,
  indented,
  tone = "hubble",
}: DevMenuRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={rowClassName(tone, indented)}
      style={TONE_INLINE_STYLES[tone]}
    >
      {children}
    </button>
  );
}

interface DevToggleRowProps {
  label: string;
  enabled: boolean;
  onToggle: () => void;
  indented?: boolean | undefined;
  tone?: DevMenuTone | undefined;
}

export function DevToggleRow({
  label,
  enabled,
  onToggle,
  indented,
  tone,
}: DevToggleRowProps) {
  return (
    <DevMenuRow onClick={onToggle} indented={indented} tone={tone}>
      <span>{label}</span>
      <span
        className={`inline-block h-2 w-2 rounded-full ${enabled ? "bg-green" : "bg-red"}`}
      />
    </DevMenuRow>
  );
}

interface DevValueRowProps {
  label: string;
  value: ReactNode;
  onClick?: (() => void) | undefined;
  indented?: boolean | undefined;
  tone?: DevMenuTone | undefined;
}

export function DevValueRow({
  label,
  value,
  onClick,
  indented,
  tone = "hubble",
}: DevValueRowProps) {
  return (
    <DevMenuRow onClick={onClick} indented={indented} tone={tone}>
      <span>{label}</span>
      <span className={`flex items-center gap-1 ${TONE_VALUE_CLASSES[tone]}`}>
        {value}
      </span>
    </DevMenuRow>
  );
}
